import { Configuration } from '@partful/features/configurations/model/types';
import { useUser } from '@providers/User';
import { useEffect, useMemo, useState } from 'react';
import { useCreateInstructionVersion } from '../../versions/lib/useCreateInstructionVersion';
import { useListInstructionVersions } from '../../versions/lib/useListInstructionVersions';
import { useCreateInstruction } from './useCreateInstruction';
import { useListInstructions } from './useListInstructions';

export function useInstructionForConfiguration(configuration: Configuration) {
  const {
    username: userId,
    userDetails: { name: userName },
  } = useUser();

  /**
   * Fetch instruction associated with this configuration (instructions are infinitely paged)
   */
  const { data: instructions, isError: isListInstructionsError } = useListInstructions(configuration.PID);
  const instruction = useMemo(() => instructions?.[0], [instructions]);

  /**
   * Fetch versions for the given instruction (versions are infinitely paged)
   */
  const { isError: isListInstructionVersionsError, data: versions } = useListInstructionVersions(instruction?.id);

  /**
   * Hooks and state to create an initial instruction and version if first time landing here
   */
  const [triggerCreateInstruction, setTriggerCreateInstruction] = useState(false);
  const [triggerCreateInstructionVersion, setTriggerCreateInstructionVersion] = useState(false);

  const { mutate: createInstruction, isError: isCreateInstructionError } = useCreateInstruction();
  const { mutate: createInstructionVersion, isError: isCreateInstructionVersionError } = useCreateInstructionVersion();

  /**
   * If no instruction for this configuration exists yet, create it
   */
  useEffect(() => {
    if (instructions?.length === 0 && !triggerCreateInstruction) {
      setTriggerCreateInstruction(true);
    }
  }, [instructions, triggerCreateInstruction, setTriggerCreateInstruction]);

  useEffect(() => {
    if (triggerCreateInstruction) {
      createInstruction({ configurationId: configuration.PID });
    }
  }, [triggerCreateInstruction, configuration.PID, createInstruction]);

  /**
   * If no instruction version for this instruction exists yet, create it
   */
  useEffect(() => {
    if (instruction && versions?.length === 0 && !triggerCreateInstructionVersion) {
      setTriggerCreateInstructionVersion(true);
    }
  }, [instruction, versions, triggerCreateInstructionVersion, setTriggerCreateInstructionVersion]);

  useEffect(() => {
    if (instruction && triggerCreateInstructionVersion) {
      createInstructionVersion({
        instructionId: instruction.id,
        args: {
          authorName: userName,
          authorUserId: userId!,
          cadFileName: `${configuration.PID}.zip`,
          cadUploadDate: new Date().toISOString(),
          configurationId: configuration.PID,
          configurationVersionId: configuration.configurationVersion,
          status: 'draft',
        },
      });
    }
  }, [
    triggerCreateInstructionVersion,
    createInstructionVersion,
    configuration.PID,
    configuration.configurationVersion,
    userName,
    userId,
    instruction,
  ]);

  /**
   * Simplified states for rendering
   */
  const isError =
    isListInstructionsError ||
    isListInstructionVersionsError ||
    isCreateInstructionError ||
    isCreateInstructionVersionError;
  const isLoading = !isError && (!instruction || !versions?.length);

  return { data: isError ? undefined : instruction, isError, isLoading };
}
